import { RegisterScreen } from '@/components/RegisterScreen';
import { ActivityLabour } from '@/screens/parts/ActivityLabour';
import { formatCurrency, formatDate, formatNumber, todayIso } from '@/components/live';
import {
  area, dateNotFuture, doneByFields, plotCell, plotField, speciesBelongsToPlot, speciesField, usePracticePickers,
} from '@/screens/parts/practiceFields';
import { SAMPLES, SILVICULTURE_ACTIVITIES, optionLabel } from '@/lib/practiceLists';

interface Row {
  id: string; silviculture_code: string | null; plot_code: string | null; farmer_name: string | null; village_name: string | null;
  activity: string; activity_detail: string | null; species_label: string | null; started_on: string; completed_on: string | null;
  done_by_display: string | null; done_by_type: string; workers_engaged: number | null; person_days: number | null;
  area_covered_ha: number | null; cost: number | null;
  plot_id: string | null; done_by_contractor_id: string | null;
}

/**
 * Every silviculture activity, across every plot (owner, 2026-09-25: "these all make as ui pages
 * under Plot management").
 *
 * The same rows the plot record has always shown under "Silviculture activities", read the other
 * way round. Inside a plot you ask what was done here; here you ask where the weeding got to this
 * season, which contractor did it, and how many person-days it took - questions the plot record
 * cannot answer without being opened four hundred times.
 */
export default function SilvicultureScreen() {
  const pick = usePracticePickers();
  const cutoff = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);

  return (
    <RegisterScreen<Row>
      plotFilter
      // Workers from the register and what each was paid for this activity (owner, 2026-09-27).
      extraTab={(row) => ({
        label: 'Labour',
        content: <ActivityLabour activity={{ ...row, done_by_contractor_name: row.done_by_type === 'contractor' ? row.done_by_display : null }} />,
      })}
      addLabel="Add activity" exportName="silviculture-activities"
      orderBy={{ column: 'started_on', ascending: false }}
      recordLabel={(row) => `activity ${row.silviculture_code ?? ''} - ${optionLabel(SILVICULTURE_ACTIVITIES, row.activity)} on ${row.plot_code ?? ''}`}
      screenKey="plot.inventory"
      searchColumns={['silviculture_code', 'plot_code', 'farmer_name', 'activity_detail', 'done_by_name']}
      secondary={{ sum: (row) => row.area_covered_ha ?? 0, unit: 'ha', digits: 2 }}
      subtitle="Weeding, pruning, thinning, planting and the rest - what was done on each plot, by whom, and at what cost."
      table="ops.plot_silviculture_activities" title="Silviculture activities" view="plot_silviculture_activities"
      columns={[
        { key: 'silviculture_code', label: 'Ref', render: (row) => <strong>{row.silviculture_code ?? '--'}</strong> },
        { key: 'plot_code', label: 'Plot', render: plotCell },
        { key: 'village_name', label: 'Village', render: (row) => row.village_name ?? '--' },
        { key: 'activity', label: 'Activity', render: (row) => (
          <span className="cell-stack">
            <strong>{optionLabel(SILVICULTURE_ACTIVITIES, row.activity)}</strong>
            <small className="muted">{row.activity_detail ?? row.species_label ?? ''}</small>
          </span>
        ) },
        { key: 'started_on', label: 'When', render: (row) => (
          row.completed_on && row.completed_on !== row.started_on
            ? `${formatDate(row.started_on)} to ${formatDate(row.completed_on)}`
            : formatDate(row.started_on)
        ) },
        { key: 'done_by_display', label: 'Done by', render: (row) => row.done_by_display ?? '--' },
        { key: 'person_days', label: 'Person-days', align: 'right', render: (row) => formatNumber(row.person_days, 1) },
        { key: 'area_covered_ha', label: 'Area', align: 'right', render: (row) => area(row.area_covered_ha) },
        { key: 'cost', label: 'Cost', align: 'right', render: (row) => (row.cost == null ? '--' : formatCurrency(row.cost)) },
      ]}
      stats={[
        { key: 'open', label: 'Not finished', tone: 'warn', match: (row) => !row.completed_on },
        { key: 'recent', label: 'Started in 30 days', tone: 'info', match: (row) => row.started_on >= cutoff },
        { key: 'contracted', label: 'By a contractor', tone: 'neutral', match: (row) => row.done_by_type === 'contractor' },
        { key: 'planting', label: 'Planting', tone: 'ok', match: (row) => row.activity === 'planting' },
      ]}
      defaults={{ started_on: todayIso(), done_by_type: 'farmer' }}
      fields={[
        plotField(pick.plots),
        { key: 'activity', label: 'Activity', type: 'select', options: SILVICULTURE_ACTIVITIES, required: true },
        speciesField(pick, false),
        { key: 'activity_detail', label: 'Detail', type: 'text', suggestions: SAMPLES.silvicultureDetail, wide: true },
        { key: 'started_on', label: 'Started on', section: 'Dates & extent', type: 'date', required: true, max: todayIso() },
        { key: 'completed_on', label: 'Completed on', type: 'date' },
        { key: 'area_covered_ha', label: 'Area covered (ha)', type: 'number' },
        { key: 'trees_covered', label: 'Trees covered', type: 'number' },
        ...doneByFields('done_by', pick.workers, pick.contractors).map((field, at) => (at === 0 ? { ...field, section: 'Done by & cost' } : field)),
        { key: 'workers_engaged', label: 'Workers engaged', type: 'number' },
        { key: 'person_days', label: 'Person-days', type: 'number' },
        { key: 'cost', label: 'Cost (₹)', type: 'number' },
        { key: 'photo_document_id', label: 'Photo', section: 'Evidence', type: 'file', accept: 'image/*' },
        { key: 'remarks', label: 'Remarks', type: 'textarea' },
      ]}
      validate={(form) => ({
        species_inventory_id: speciesBelongsToPlot(pick, form),
        started_on: dateNotFuture(form, 'started_on', 'The start date'),
        completed_on: form.completed_on && String(form.completed_on) < String(form.started_on ?? '')
          ? 'Cannot finish before it started.' : dateNotFuture(form, 'completed_on', 'The completion date'),
      })}
    />
  );
}
